'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { HeroSlide } from '@/lib/types';
import { cn } from '@/lib/utils';

interface HeroProps {
  slides: HeroSlide[];
}

export default function Hero({ slides }: Readonly<HeroProps>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const paginate = useCallback(
    (newDirection: number) => {
      setDirection(newDirection);
      setCurrentIndex((prev) => (prev + newDirection + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 6000);
    return () => clearInterval(timer);
  }, [paginate]);

  const currentSlide = slides[currentIndex];

  return (
    <section className="bg-navy-dark relative h-[85vh] min-h-[600px] w-full overflow-hidden">
      <h1 className="sr-only">Dynamic Club, club de gymnastique à Braine-l&apos;Alleud</h1>
      {/* Background with cross-fade */}
      <AnimatePresence mode="wait">
        <m.div
          key={`bg-${currentIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0">
            <picture>
              <source
                media="(max-width: 640px)"
                srcSet={currentSlide.image.replace(/\.webp$/, '.mobile.webp')}
              />
              <Image
                src={currentSlide.image}
                alt={currentSlide.title ?? 'hero'}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1600px"
                className="object-cover"
                priority={currentIndex === 0}
                quality={75}
                loading="eager"
              />
            </picture>
            <div className="from-navy-dark absolute inset-0 bg-gradient-to-r to-transparent opacity-80" />
          </div>
        </m.div>
      </AnimatePresence>

      <div className="relative z-10 container mx-auto flex h-full flex-col justify-center px-4">
        <AnimatePresence mode="wait">
          <m.div
            key={currentIndex}
            initial={{ opacity: 0, x: direction > 0 ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -50 : 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 100 }}
            className="max-w-2xl"
          >
            <span className="bg-red mb-4 inline-block rounded px-3 py-1 text-xs font-bold tracking-widest text-white uppercase">
              {currentSlide.category}
            </span>
            <p className="mb-6 text-5xl leading-[0.9] font-black tracking-tight text-white uppercase md:text-7xl">
              {currentSlide.title}
            </p>
            <p className="mb-8 max-w-lg text-xl font-light text-white/80">
              {currentSlide.subtitle}
            </p>
            <Link
              href={currentSlide.buttonUrl}
              className="text-navy hover:bg-red inline-flex transform items-center gap-2 rounded bg-white px-8 py-4 font-black tracking-wider uppercase transition-all hover:-translate-y-1 hover:text-white active:translate-y-0"
            >
              {currentSlide.buttonLabel}
            </Link>
          </m.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="absolute right-0 bottom-12 left-0 z-20">
        <div className="container mx-auto flex items-center justify-between px-4">
          <div className="flex gap-2">
            {slides.map((slide, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Afficher le contenu ${index + 1} : ${slide.title}`}
                aria-current={index === currentIndex ? 'true' : undefined}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={cn(
                  'h-1 rounded-full transition-all duration-300',
                  index === currentIndex ? 'bg-red w-12' : 'w-6 bg-white/30 hover:bg-white/50',
                )}
              />
            ))}
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              aria-label="Afficher le contenu précédent"
              onClick={() => paginate(-1)}
              className="rounded-full border border-white/20 bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              aria-label="Afficher le contenu suivant"
              onClick={() => paginate(1)}
              className="rounded-full border border-white/20 bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
